/**
 * ConsultantHubSection — the team-side "Consultant Hub" branch in the outline
 * (docs/CONSULTANT_HUB.md). A top-level project branch, right after the Client Hub.
 *
 * Children: every background release for the project, newest first (no phase grouping —
 * Matt, 2026-10-14), each expanding to who on the Hub opened it, downloaded files or walked
 * the model; then a People branch — the consultant team members with their own link + PIN.
 * Add people / per-person actions live in the ⋯ menus. Lazy-loads when first expanded and
 * reloads on 'consultant-hub-updated'.
 */
import { useEffect, useReducer, useRef, useState } from 'react'
import { wf, saveViewState } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider, useActionMenu } from './ActionMenu.jsx'
import { confirmDialog, alertDialog } from './Dialog.jsx'
import { showPill } from '../outline/utils.js'
import {
  listHub, revokeHubPerson, reactivateHubPerson, deleteHubPerson, resendHubAccess,
  regenerateHubAccess, setHubPersonDiscipline, HUB_DISCIPLINES,
} from '../services/consultantHub.js'

const ROOT_KEY = 'root:consultant-hub'
const MONO = '"DM Mono",monospace'
const pad2 = (n) => String(n).padStart(2, '0')
const disciplineLabel = (k) => HUB_DISCIPLINES.find((d) => d.key === k)?.label || null

function fmtShort(iso) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) } catch { return '' }
}

function calcVis() {
  if (!wf.zoomProjectId) return 'hidden'
  if (!wf.zoomRoot && !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomSection &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId && !wf.searchQuery) return 'home'
  return 'hidden'
}

function toggleKey(key) {
  wf.collapsed[key] = wf.collapsed[key] === false
  saveViewState()
  window.dispatchEvent(new Event('outline-render'))
}

async function copy(text, what) {
  try { await navigator.clipboard.writeText(text) } catch { /* clipboard blocked */ }
  showPill(what + ' copied')
}

export default function ConsultantHubSection() {
  const [, forceUpdate] = useReducer((v) => v + 1, 0)
  const [data, setData] = useState(null)   // null = not loaded; { people, releases } | { error }
  const loadedRef = useRef(false)
  const projId = wf.zoomProjectId || ctx.recordId || ''

  useEffect(() => {
    const onRender = () => forceUpdate()
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [])

  function load() {
    if (!projId) return
    listHub(projId).then(setData).catch((e) => setData({ error: e.message || 'Could not load the Hub', people: [], releases: [] }))
  }

  useEffect(() => {
    const onUpdated = () => { if (loadedRef.current) load() }
    window.addEventListener('consultant-hub-updated', onUpdated)
    return () => window.removeEventListener('consultant-hub-updated', onUpdated)
  }) // eslint-disable-line react-hooks/exhaustive-deps

  const projRef = useRef(projId)
  if (projRef.current !== projId) { projRef.current = projId; loadedRef.current = false; if (data) setData(null) }

  const collapsed = wf.collapsed[ROOT_KEY] !== false
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!collapsed && !loadedRef.current && projId) { loadedRef.current = true; load() } })

  if (calcVis() === 'hidden') return null

  const people = data?.people || []
  const releases = data?.releases || []
  const peopleKey = 'chub:people:' + projId

  function addPeople() {
    window.dispatchEvent(new CustomEvent('open-consultant-hub-access', {
      detail: { projectId: projId, existingEmails: people.map((p) => p.email) },
    }))
  }
  function setAll(open) {
    wf.collapsed[ROOT_KEY] = !open
    wf.collapsed[peopleKey] = !open
    releases.forEach((r) => { wf.collapsed['chub:rel:' + r.id] = !open })
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }

  const active = people.filter((p) => p.status === 'active').length

  return (
    <div className="wf-list">
      <HubRoot collapsed={collapsed} count={data ? active : null} onAdd={addPeople}
        onExpandAll={() => setAll(true)} onCollapseAll={() => setAll(false)} />
      {!collapsed && (
        <div className="wf-children">
          {data === null && <div className="wf-node-text" style={{ padding: '6px 0', color: '#7a9aaa' }}>Loading…</div>}
          {data?.error && <div className="wf-node-text" style={{ padding: '6px 0', color: '#b3372a' }}>{data.error}</div>}
          {data && !data.error && releases.length === 0 && (
            <div className="wf-node-text" style={{ padding: '4px 0', color: '#7a9aaa', fontStyle: 'italic' }}>
              No backgrounds released yet. Issue one from AlderCAD with Backgrounds…
            </div>
          )}
          {releases.map((r, i) => <ReleaseNode key={r.id} rel={r} current={i === 0} people={people} />)}
          {data && !data.error && (
            <PeopleBranch ckey={peopleKey} people={people} onAdd={addPeople} onChanged={load} />
          )}
        </div>
      )}
    </div>
  )
}

function HubRoot({ collapsed, count, onAdd, onExpandAll, onCollapseAll }) {
  const menu = useActionMenu()
  return (
    <div className={`wf-node wf-node--root${collapsed ? ' wf-node--collapsed' : ''}`} data-key={ROOT_KEY}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={ROOT_KEY}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--root" data-fn="toggleCollapse" data-key={ROOT_KEY}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--root">Consultant Hub</span>
          {count != null && <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{count} {count === 1 ? 'person' : 'people'}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); onAdd() }}>Add people…</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => { menu.close(); onExpandAll() }}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={() => { menu.close(); onCollapseAll() }}>Collapse all</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

/** one release; expands to who on the Hub has opened it */
function ReleaseNode({ rel, current, people }) {
  const menu = useActionMenu()
  const key = 'chub:rel:' + rel.id
  const collapsed = wf.collapsed[key] !== false
  const act = rel.activity?.people || {}
  const seen = people.filter((p) => act[p.id]).length
  return (
    <div className={`wf-node wf-node--item${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={() => toggleKey(key)}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <span className="wf-bullet wf-bullet--item" onClick={() => toggleKey(key)}></span>
        <div className="wf-text-wrap">
          <span style={{ fontFamily: MONO, fontSize: 12, color: '#7a9aaa', marginRight: 8 }}>BG{pad2(rel.number)}</span>
          <span className="wf-node-text">{rel.title}</span>
          {current && <span className="wf-badge wf-badge--green" style={{ marginLeft: 8 }}>Current</span>}
          {rel.importantCount > 0 && <span className="wf-badge wf-badge--amber" style={{ marginLeft: 6 }}>Grids or levels changed</span>}
          {people.length > 0 && <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{seen} of {people.length} opened</span>}
          <span style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 11, color: '#9bb0bb', flexShrink: 0, paddingLeft: 12 }}>{fmtShort(rel.issuedAt)}</span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); window.open(rel.link, '_blank', 'noopener') }}>Open release page</ActionMenuItem>
        <ActionMenuItem onClick={() => { menu.close(); copy(rel.link, 'Release link') }}>Copy release link</ActionMenuItem>
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          {people.length === 0 && (
            <div className="wf-node-text" style={{ padding: '4px 0', color: '#7a9aaa', fontStyle: 'italic' }}>No one is on the Hub yet.</div>
          )}
          {people.map((p) => {
            const a = act[p.id]
            const parts = a ? [
              a.opens && `opened ${a.opens}×`,
              a.downloads && `${a.downloads} download${a.downloads === 1 ? '' : 's'}`,
              a.models && 'walked the model',
            ].filter(Boolean).join(' · ') : ''
            return (
              <div key={p.id} className="wf-node wf-node--item" data-key={key + ':' + p.id}>
                <div className="wf-row">
                  <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                  <span className="wf-bullet wf-bullet--item" style={a ? undefined : { opacity: 0.4 }}></span>
                  <div className="wf-text-wrap">
                    <span className="wf-node-text" style={a ? undefined : { color: '#9bb0bb' }}>{p.name}</span>
                    {p.orgName && <span style={{ marginLeft: 8, fontSize: 12, color: '#9bb0bb' }}>{p.orgName}</span>}
                    <span style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 11, color: '#9bb0bb', paddingLeft: 12 }}>
                      {a ? `${parts}${a.lastAt ? ' · ' + fmtShort(a.lastAt) : ''}` : 'Not opened'}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function PeopleBranch({ ckey, people, onAdd, onChanged }) {
  const menu = useActionMenu()
  const collapsed = wf.collapsed[ckey] !== false
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={ckey}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={() => toggleKey(ckey)}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" onClick={() => toggleKey(ckey)}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">People</span>
          <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{people.length}</span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); onAdd() }}>Add people…</ActionMenuItem>
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          {people.length === 0 && (
            <div className="wf-node-text" style={{ padding: '4px 0', color: '#7a9aaa', fontStyle: 'italic' }}>
              No one yet. Use ⋯ Add people to give the project's consultants their own links.
            </div>
          )}
          {people.map((p) => <PersonRow key={p.id} person={p} onChanged={onChanged} />)}
        </div>
      )}
    </div>
  )
}

function PersonRow({ person: p, onChanged }) {
  const menu = useActionMenu()
  const revoked = p.status !== 'active'
  const disc = disciplineLabel(p.discipline)

  async function run(fn, okText) {
    menu.close()
    try { await fn(); onChanged(); if (okText) showPill(okText) }
    catch (e) { await alertDialog({ title: 'Consultant Hub', message: e.message || 'Something went wrong.' }) }
  }
  async function regenerate() {
    menu.close()
    if (!(await confirmDialog({
      title: 'New link and access code?',
      message: `${p.name}'s current link and code stop working. A new pair is emailed to ${p.email}.`,
      confirmLabel: 'Send new link',
    }))) return
    run(() => regenerateHubAccess(p.id), 'New link sent')
  }
  async function remove() {
    menu.close()
    if (!(await confirmDialog({ title: 'Delete person?', message: `Delete ${p.name} from the Hub? Their past activity stays on the releases, unnamed.`, confirmLabel: 'Delete', danger: true }))) return
    run(() => deleteHubPerson(p.id))
  }

  return (
    <div className="wf-node wf-node--item" data-key={'chub:person:' + p.id}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={revoked ? { opacity: 0.4 } : undefined}></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={revoked ? { color: '#9bb0bb', textDecoration: 'line-through' } : undefined}>{p.name}</span>
          {p.orgName && <span style={{ marginLeft: 8, fontSize: 12, color: '#9bb0bb' }}>{p.orgName}</span>}
          {disc && <span className="wf-badge wf-badge--blue" style={{ marginLeft: 8 }}>{disc}</span>}
          {revoked && <span className="wf-badge wf-badge--gray" style={{ marginLeft: 6 }}>Access removed</span>}
          {!revoked && !p.subscribed && <span className="wf-badge wf-badge--gray" style={{ marginLeft: 6 }}>Emails off</span>}
          <span style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 11, color: '#9bb0bb', paddingLeft: 12 }}>
            {p.lastOpenedAt ? 'Last visit ' + fmtShort(p.lastOpenedAt) : 'Never opened'}
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        {!revoked && (
          <>
            <ActionMenuItem onClick={() => { menu.close(); copy(p.link, 'Hub link') }}>Copy their link</ActionMenuItem>
            <ActionMenuItem onClick={() => { menu.close(); alertDialog({ title: `${p.name}'s access code`, message: `${p.pin}\n\nSent to ${p.email}.` }) }}>Show access code</ActionMenuItem>
            <ActionMenuItem onClick={() => run(() => resendHubAccess(p.id), 'Access email sent')}>Resend access email</ActionMenuItem>
            <ActionMenuItem onClick={regenerate}>New link and code…</ActionMenuItem>
            <ActionMenuDivider />
            {HUB_DISCIPLINES.map((d) => (
              <ActionMenuItem key={d.key} onClick={() => run(() => setHubPersonDiscipline(p.id, p.discipline === d.key ? null : d.key))}>
                {p.discipline === d.key ? '✓ ' : ''}Opens to {d.label}
              </ActionMenuItem>
            ))}
            <ActionMenuDivider />
            <ActionMenuItem danger onClick={() => run(() => revokeHubPerson(p.id))}>Remove access</ActionMenuItem>
          </>
        )}
        {revoked && (
          <>
            <ActionMenuItem onClick={() => run(() => reactivateHubPerson(p.id))}>Restore access</ActionMenuItem>
            <ActionMenuItem danger onClick={remove}>Delete…</ActionMenuItem>
          </>
        )}
      </ActionMenu>
    </div>
  )
}
